import React, { useCallback, useContext, useState } from "react";
import uuid from "react-uuid";
import { WithChildren, WithClassName } from "types";
import { Accordion, AccordionProps } from "../components";

interface CommonAccordionProps
  extends WithChildren,
    WithClassName,
    Pick<AccordionProps, "isAbsolute"> {}

export type ContextAccordion = (
  props: CommonAccordionProps
) => JSX.Element | null;

interface AccordionControls {
  open: () => void;
  close: () => void;
  switch: () => void;
  closeAllAndSwitch: () => void;
}

interface ProviderProps {
  createAccordion: (key?: string) => AccordionControls & {
    element: ContextAccordion;
    key: string;
  };
  getControls: (key: string) => AccordionControls;
  isOpen: (key: string) => boolean;
  closeAll: () => void;
}

interface Controls {
  [key: string]: boolean;
}

const AccordionContext = React.createContext({} as ProviderProps);

export function useAccordionContext() {
  return useContext(AccordionContext);
}

export const AccordionProvider = ({ children }: WithChildren) => {
  const [controls, setControls] = useState<Controls>({});

  const _open = useCallback(
    (key: string) => setControls((values) => ({ ...values, [key]: true })),
    [setControls]
  );

  const _close = useCallback(
    (key: string) => setControls((values) => ({ ...values, [key]: false })),
    [setControls]
  );

  const _switch = useCallback(
    (key: string) =>
      setControls((values) => ({ ...values, [key]: !values[key] })),
    [setControls]
  );

  const closeAll = useCallback(
    () =>
      setControls((values) =>
        Object.fromEntries(Object.keys(values).map((_key) => [_key, false]))
      ),
    []
  );

  const isOpen = (key: string) => controls[key];

  const _closeAllAndSwitch = (key: string) => {
    if (isOpen(key)) {
      closeAll();
    } else {
      closeAll();
      _open(key);
    }
  };

  const getControls = (key: string) => ({
    open: () => _open(key),
    close: () => _close(key),
    switch: () => _switch(key),
    closeAllAndSwitch: () => _closeAllAndSwitch(key),
  });

  const createAccordion = useCallback((predefinedKey?: string) => {
    const key = predefinedKey || uuid();

    return {
      element: ({
        children: accordionChildren,
        ...props
      }: CommonAccordionProps) => {
        return (
          <Accordion id={key} {...props}>
            {accordionChildren}
          </Accordion>
        );
      },
      key,
      ...getControls(key),
    };
  }, []);

  return (
    <AccordionContext.Provider
      value={{ createAccordion, getControls, isOpen, closeAll }}
    >
      {children}
    </AccordionContext.Provider>
  );
};
